import React, { useState } from 'react';
import { DollarSignIcon, LayersIcon, UserIcon, UsersIcon, BuildingIcon, HomeIcon, TagIcon, GitBranchIcon, ArrowUpRightIcon, AlertTriangleIcon, ClockIcon, EyeIcon, CheckCircleIcon, LockIcon, CalendarIcon, PercentIcon, HelpCircleIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, SaveIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
type FDOAMode = 'Override' | 'Satisfy' | 'CostCentreOnly';
type FDOARuleProps = {
  rule: any;
  onSave: (rule: any) => void;
  onCancel: () => void;
  locale: string;
};
export function FDOARuleEditor({
  rule,
  onSave,
  onCancel,
  locale
}: FDOARuleProps) {
  // Initialize state with rule data or defaults
  const [ruleData, setRuleData] = useState({
    ...rule,
    modes: rule.modes || {
      vendorMode: 'Satisfy' as FDOAMode,
      siteMode: 'Satisfy' as FDOAMode,
      entityMode: 'Override' as FDOAMode
    },
    delegation: rule.delegation || {
      inheritUpperLimit: true,
      useOriginalChain: true,
      maxHops: 5
    },
    fourEyes: rule.fourEyes || {
      enabled: true,
      threshold: 10000,
      applyAt: 'Both',
      currency: 'AUD'
    },
    emergency: rule.emergency || {
      enabled: false,
      approvers: []
    },
    lineLevel: rule.lineLevel || {
      enabled: true,
      evaluationMode: 'Sequential',
      categoriesOnly: true
    },
    preApproval: rule.preApproval || {
      checkClosedPeriods: true
    },
    multiCurrency: rule.multiCurrency || {
      enabled: true,
      baseCurrency: 'AUD',
      evaluateAt: 'Header'
    },
    audit: rule.audit || {
      enabled: true,
      storeDecisions: true
    }
  });
  // Sample data for dropdowns
  const [entities] = useState([{
    id: 'entity-001',
    name: 'Global Technologies Inc.'
  }, {
    id: 'entity-002',
    name: 'European Operations Ltd'
  }, {
    id: 'entity-003',
    name: 'APAC Subsidiary'
  }]);
  const [users] = useState([{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }]);
  const [roles] = useState([{
    id: 'role-001',
    name: 'Finance Manager'
  }, {
    id: 'role-002',
    name: 'Department Head'
  }, {
    id: 'role-003',
    name: 'CEO'
  }, {
    id: 'role-004',
    name: 'CFO'
  }]);
  const [glCategories] = useState([{
    id: 'cat-001',
    code: '1000',
    description: 'IT Equipment',
    entityId: 'entity-001'
  }, {
    id: 'cat-002',
    code: '2000',
    description: 'Office Supplies',
    entityId: 'entity-001'
  }, {
    id: 'cat-003',
    code: '3000',
    description: 'Professional Services',
    entityId: 'entity-001'
  }, {
    id: 'cat-004',
    code: '4000',
    description: 'Travel Expenses',
    entityId: 'entity-001'
  }]);
  const [currencies] = useState([{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }]);
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    defaults: true,
    modes: true,
    delegation: true,
    categories: true,
    emergency: false,
    fourEyes: true,
    lineLevel: false,
    periods: false,
    multiCurrency: false,
    audit: false
  });
  const [defaultApprovers, setDefaultApprovers] = useState(rule.defaultApprovers || [{
    id: 'default-001',
    scopeType: 'Entity',
    scopeId: 'entity-001',
    userOrRoleId: 'role-001',
    isReviewer: false,
    isApprover: true,
    fdoaLimit: 10000,
    currency: 'AUD',
    belongsToCategories: ['cat-001', 'cat-002']
  }, {
    id: 'default-002',
    scopeType: 'Vendor',
    scopeId: 'vendor-001',
    userOrRoleId: 'user-002',
    isReviewer: true,
    isApprover: false,
    fdoaLimit: 0,
    currency: 'AUD',
    belongsToCategories: []
  }]);
  const [categoryApprovers, setCategoryApprovers] = useState(rule.categoryApprovers || [{
    id: 'catapp-001',
    categoryId: 'cat-001',
    sequence: 1,
    userOrRoleId: 'user-001',
    limit: 5000,
    currency: 'AUD'
  }, {
    id: 'catapp-002',
    categoryId: 'cat-001',
    sequence: 2,
    userOrRoleId: 'role-001',
    limit: 25000,
    currency: 'AUD'
  }, {
    id: 'catapp-003',
    categoryId: 'cat-001',
    sequence: 3,
    userOrRoleId: 'role-003',
    limit: 100000,
    currency: 'AUD'
  }]);
  const [accountingPeriods, setAccountingPeriods] = useState(rule.accountingPeriods || [{
    id: 'period-001',
    entityId: 'entity-001',
    periodCode: 'FY23-Q3',
    startUtc: '2023-07-01T00:00:00Z',
    endUtc: '2023-09-30T23:59:59Z',
    isClosed: true
  }, {
    id: 'period-002',
    entityId: 'entity-001',
    periodCode: 'FY23-Q4',
    startUtc: '2023-10-01T00:00:00Z',
    endUtc: '2023-12-31T23:59:59Z',
    isClosed: false
  }]);
  // Helper functions
  const toggleSection = (section: string) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  const handleModeChange = (scope: 'vendorMode' | 'siteMode' | 'entityMode', mode: FDOAMode) => {
    setRuleData({
      ...ruleData,
      modes: {
        ...ruleData.modes,
        [scope]: mode
      }
    });
  };
  const handleDelegationChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      delegation: {
        ...ruleData.delegation,
        [field]: value
      }
    });
  };
  const handleFourEyesChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      fourEyes: {
        ...ruleData.fourEyes,
        [field]: value
      }
    });
  };
  const handleEmergencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        [field]: value
      }
    });
  };
  const handleLineLevelChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      lineLevel: {
        ...ruleData.lineLevel,
        [field]: value
      }
    });
  };
  const handleMultiCurrencyChange = (field: string, value: any) => {
    setRuleData({
      ...ruleData,
      multiCurrency: {
        ...ruleData.multiCurrency,
        [field]: value
      }
    });
  };
  const addDefaultApprover = () => {
    const newApprover = {
      id: `default-${Date.now()}`,
      scopeType: 'Entity',
      scopeId: '',
      userOrRoleId: '',
      isReviewer: false,
      isApprover: true,
      fdoaLimit: 0,
      currency: 'AUD',
      belongsToCategories: []
    };
    setDefaultApprovers([...defaultApprovers, newApprover]);
  };
  const updateDefaultApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setDefaultApprovers(updatedApprovers);
  };
  const removeDefaultApprover = (index: number) => {
    const updatedApprovers = [...defaultApprovers];
    updatedApprovers.splice(index, 1);
    setDefaultApprovers(updatedApprovers);
  };
  const addCategoryApprover = () => {
    const newApprover = {
      id: `catapp-${Date.now()}`,
      categoryId: '',
      sequence: categoryApprovers.length + 1,
      userOrRoleId: '',
      limit: 0,
      currency: 'AUD'
    };
    setCategoryApprovers([...categoryApprovers, newApprover]);
  };
  const updateCategoryApprover = (index: number, field: string, value: any) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers[index] = {
      ...updatedApprovers[index],
      [field]: value
    };
    setCategoryApprovers(updatedApprovers);
  };
  const removeCategoryApprover = (index: number) => {
    const updatedApprovers = [...categoryApprovers];
    updatedApprovers.splice(index, 1);
    setCategoryApprovers(updatedApprovers);
  };
  const addEmergencyApprover = (userOrRoleId: string) => {
    if (!ruleData.emergency.approvers.includes(userOrRoleId)) {
      setRuleData({
        ...ruleData,
        emergency: {
          ...ruleData.emergency,
          approvers: [...ruleData.emergency.approvers, userOrRoleId]
        }
      });
    }
  };
  const removeEmergencyApprover = (userOrRoleId: string) => {
    setRuleData({
      ...ruleData,
      emergency: {
        ...ruleData.emergency,
        approvers: ruleData.emergency.approvers.filter(id => id !== userOrRoleId)
      }
    });
  };
  const addAccountingPeriod = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const newPeriod = {
      id: `period-${Date.now()}`,
      entityId: 'entity-001',
      periodCode: `FY${today.getFullYear().toString().substr(2)}-Q${Math.floor(today.getMonth() / 3) + 1}`,
      startUtc: today.toISOString(),
      endUtc: nextMonth.toISOString(),
      isClosed: false
    };
    setAccountingPeriods([...accountingPeriods, newPeriod]);
  };
  const updateAccountingPeriod = (index: number, field: string, value: any) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods[index] = {
      ...updatedPeriods[index],
      [field]: value
    };
    setAccountingPeriods(updatedPeriods);
  };
  const removeAccountingPeriod = (index: number) => {
    const updatedPeriods = [...accountingPeriods];
    updatedPeriods.splice(index, 1);
    setAccountingPeriods(updatedPeriods);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedRule = {
      ...rule,
      ...ruleData,
      defaultApprovers,
      categoryApprovers,
      accountingPeriods
    };
    onSave(updatedRule);
  };
  // Render the form
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-9708">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9709">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center" data-id="element-9710">
          <div data-id="element-9711">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9712">
              Financial Delegation Of Authority (FDOA) Rule Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9713">
              Configure approval pathways based on financial delegation limits
              and cost centers
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-9714">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9715">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9716">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-9717" />
              Save Rule
            </button>
          </div>
        </div>
        {/* Basic Information */}
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-9718">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9719">
            Basic Information
          </h4>
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9720">
            <div className="sm:col-span-3" data-id="element-9721">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-9722">
                Rule Name
              </label>
              <div className="mt-1" data-id="element-9723">
                <input type="text" id="name" value={ruleData.name} onChange={e => setRuleData({
                ...ruleData,
                name: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9724" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-9725">
              <label htmlFor="version" className="block text-sm font-medium text-gray-700" data-id="element-9726">
                Version
              </label>
              <div className="mt-1" data-id="element-9727">
                <input type="text" id="version" value={ruleData.version} onChange={e => setRuleData({
                ...ruleData,
                version: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9728" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-9729">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-9730">
                Description
              </label>
              <div className="mt-1" data-id="element-9731">
                <textarea id="description" rows={2} value={ruleData.description} onChange={e => setRuleData({
                ...ruleData,
                description: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9732" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-9733">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-9734">
                Status
              </label>
              <div className="mt-1" data-id="element-9735">
                <select id="status" value={ruleData.status} onChange={e => setRuleData({
                ...ruleData,
                status: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9736">
                  <option value="Draft" data-id="element-9737">Draft</option>
                  <option value="Active" data-id="element-9738">Active</option>
                  <option value="Inactive" data-id="element-9739">Inactive</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-9740">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-9741">
                Scope
              </label>
              <div className="mt-1" data-id="element-9742">
                <select id="scope" value={ruleData.scope} onChange={e => setRuleData({
                ...ruleData,
                scope: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9743">
                  <option value="Entity" data-id="element-9744">Entity</option>
                  <option value="Global" data-id="element-9745">Global</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* Default Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-9746">
          <button type="button" onClick={() => toggleSection('defaults')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9747">
            <div className="flex items-center" data-id="element-9748">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9749" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9750">
                Default Approvers (Vendor/Site/Entity)
              </h3>
            </div>
            {expandedSections.defaults ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9751" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9752" />}
          </button>
          {expandedSections.defaults && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9753">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9754">
                Configure default approvers for vendors, sites, and entities.
                These can be reviewers, approvers, or both.
              </p>
              <div className="space-y-4" data-id="element-9755">
                {defaultApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-9756">
                    <div className="flex justify-between items-start" data-id="element-9757">
                      <div className="flex items-center" data-id="element-9758">
                        {approver.scopeType === 'Entity' ? <BuildingIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-9759" /> : approver.scopeType === 'Site' ? <HomeIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-9760" /> : <TagIcon className="h-5 w-5 text-orange-500 mr-2" data-id="element-9761" />}
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-9762">
                          {approver.scopeType} Default Approver
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeDefaultApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-9763">
                        <TrashIcon className="h-4 w-4" data-id="element-9764" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9765">
                      <div className="sm:col-span-2" data-id="element-9766">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9767">
                          Scope Type
                        </label>
                        <select value={approver.scopeType} onChange={e => updateDefaultApprover(index, 'scopeType', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9768">
                          <option value="Entity" data-id="element-9769">Entity</option>
                          <option value="Site" data-id="element-9770">Site</option>
                          <option value="Vendor" data-id="element-9771">Vendor</option>
                        </select>
                      </div>
                      <div className="sm:col-span-4" data-id="element-9772">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9773">
                          {approver.scopeType} Name
                        </label>
                        <select value={approver.scopeId} onChange={e => updateDefaultApprover(index, 'scopeId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9774">
                          <option value="" data-id="element-9775">Select {approver.scopeType}</option>
                          {approver.scopeType === 'Entity' && entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-9776">
                                {entity.name}
                              </option>)}
                          {/* Add similar options for Site and Vendor when available */}
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-9777">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9778">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateDefaultApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9779">
                          <option value="" data-id="element-9780">Select User or Role</option>
                          <optgroup label="Users" data-id="element-9781">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-9782">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-9783">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-9784">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-9785">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9786">
                          Approval Type
                        </label>
                        <div className="mt-2 flex items-center space-x-4" data-id="element-9787">
                          <div className="flex items-center" data-id="element-9788">
                            <input id={`reviewer-${approver.id}`} type="checkbox" checked={approver.isReviewer} onChange={e => updateDefaultApprover(index, 'isReviewer', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9789" />
                            <label htmlFor={`reviewer-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-9790">
                              Reviewer
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-9791">
                            <input id={`approver-${approver.id}`} type="checkbox" checked={approver.isApprover} onChange={e => updateDefaultApprover(index, 'isApprover', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9792" />
                            <label htmlFor={`approver-${approver.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-9793">
                              Approver
                            </label>
                          </div>
                        </div>
                      </div>
                      {approver.isApprover && <>
                          <div className="sm:col-span-2" data-id="element-9794">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-9795">
                              FDOA Limit
                            </label>
                            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-9796">
                              <input type="number" value={approver.fdoaLimit} onChange={e => updateDefaultApprover(index, 'fdoaLimit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-3 pr-12 sm:text-sm border-gray-300 rounded-md" data-id="element-9797" />
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-9798">
                                <span className="text-gray-500 sm:text-sm" data-id="element-9799">
                                  {approver.currency}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sm:col-span-2" data-id="element-9800">
                            <label className="block text-xs font-medium text-gray-700" data-id="element-9801">
                              Currency
                            </label>
                            <select value={approver.currency} onChange={e => updateDefaultApprover(index, 'currency', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9802">
                              {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-9803">
                                  {currency.code}
                                </option>)}
                            </select>
                          </div>
                        </>}
                      <div className="sm:col-span-6" data-id="element-9804">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9805">
                          Belongs to Categories (Optional)
                        </label>
                        <select multiple value={approver.belongsToCategories} onChange={e => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    updateDefaultApprover(index, 'belongsToCategories', selected);
                  }} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" size={3} data-id="element-9806">
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-9807">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-9808">
                          Hold Ctrl/Cmd to select multiple categories
                        </p>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addDefaultApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9809">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-9810" />
                  Add Default Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Modes Section */}
        <div className="border-b border-gray-200" data-id="element-9811">
          <button type="button" onClick={() => toggleSection('modes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9812">
            <div className="flex items-center" data-id="element-9813">
              <GitBranchIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9814" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9815">
                Approval Modes
              </h3>
            </div>
            {expandedSections.modes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9816" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9817" />}
          </button>
          {expandedSections.modes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9818">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9819">
                Configure how default approvers interact with cost center
                approvers
              </p>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-9820">
                <div data-id="element-9821">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9822">
                    Vendor Mode
                  </label>
                  <div className="space-y-2" data-id="element-9823">
                    <div className="flex items-center" data-id="element-9824">
                      <input id="vendor-override" type="radio" checked={ruleData.modes.vendorMode === 'Override'} onChange={() => handleModeChange('vendorMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9825" />
                      <label htmlFor="vendor-override" className="ml-3 block text-sm text-gray-700" data-id="element-9826">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9827">
                      <input id="vendor-satisfy" type="radio" checked={ruleData.modes.vendorMode === 'Satisfy'} onChange={() => handleModeChange('vendorMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9828" />
                      <label htmlFor="vendor-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-9829">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9830">
                      <input id="vendor-costcentre" type="radio" checked={ruleData.modes.vendorMode === 'CostCentreOnly'} onChange={() => handleModeChange('vendorMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9831" />
                      <label htmlFor="vendor-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-9832">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-9833">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9834">
                    Site Mode
                  </label>
                  <div className="space-y-2" data-id="element-9835">
                    <div className="flex items-center" data-id="element-9836">
                      <input id="site-override" type="radio" checked={ruleData.modes.siteMode === 'Override'} onChange={() => handleModeChange('siteMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9837" />
                      <label htmlFor="site-override" className="ml-3 block text-sm text-gray-700" data-id="element-9838">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9839">
                      <input id="site-satisfy" type="radio" checked={ruleData.modes.siteMode === 'Satisfy'} onChange={() => handleModeChange('siteMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9840" />
                      <label htmlFor="site-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-9841">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9842">
                      <input id="site-costcentre" type="radio" checked={ruleData.modes.siteMode === 'CostCentreOnly'} onChange={() => handleModeChange('siteMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9843" />
                      <label htmlFor="site-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-9844">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
                <div data-id="element-9845">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9846">
                    Entity Mode
                  </label>
                  <div className="space-y-2" data-id="element-9847">
                    <div className="flex items-center" data-id="element-9848">
                      <input id="entity-override" type="radio" checked={ruleData.modes.entityMode === 'Override'} onChange={() => handleModeChange('entityMode', 'Override')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9849" />
                      <label htmlFor="entity-override" className="ml-3 block text-sm text-gray-700" data-id="element-9850">
                        Override
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9851">
                      <input id="entity-satisfy" type="radio" checked={ruleData.modes.entityMode === 'Satisfy'} onChange={() => handleModeChange('entityMode', 'Satisfy')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9852" />
                      <label htmlFor="entity-satisfy" className="ml-3 block text-sm text-gray-700" data-id="element-9853">
                        Satisfy
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-9854">
                      <input id="entity-costcentre" type="radio" checked={ruleData.modes.entityMode === 'CostCentreOnly'} onChange={() => handleModeChange('entityMode', 'CostCentreOnly')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-9855" />
                      <label htmlFor="entity-costcentre" className="ml-3 block text-sm text-gray-700" data-id="element-9856">
                        Cost Centre Only
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-gray-50 p-4 rounded-md" data-id="element-9857">
                <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-9858">
                  Mode Explanations:
                </h5>
                <ul className="text-xs text-gray-600 space-y-2" data-id="element-9859">
                  <li data-id="element-9860">
                    <span className="font-semibold" data-id="element-9861">Override:</span> Default
                    approvers at this level will replace cost centre approvers
                  </li>
                  <li data-id="element-9862">
                    <span className="font-semibold" data-id="element-9863">Satisfy:</span> Default
                    approvers count as part of category sequence if they belong
                    to the category
                  </li>
                  <li data-id="element-9864">
                    <span className="font-semibold" data-id="element-9865">Cost Centre Only:</span>{' '}
                    Ignore default approvers at this level, use only cost centre
                    approvers
                  </li>
                </ul>
              </div>
            </div>}
        </div>
        {/* Delegation & Escalation Section */}
        <div className="border-b border-gray-200" data-id="element-9866">
          <button type="button" onClick={() => toggleSection('delegation')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9867">
            <div className="flex items-center" data-id="element-9868">
              <ArrowUpRightIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9869" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9870">
                Delegation & Escalation
              </h3>
            </div>
            {expandedSections.delegation ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9871" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9872" />}
          </button>
          {expandedSections.delegation && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9873">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9874">
                Configure delegation and escalation settings
              </p>
              <div className="space-y-4" data-id="element-9875">
                <div className="flex items-center" data-id="element-9876">
                  <input id="inherit-limit" type="checkbox" checked={ruleData.delegation.inheritUpperLimit} onChange={e => handleDelegationChange('inheritUpperLimit', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9877" />
                  <label htmlFor="inherit-limit" className="ml-3 block text-sm text-gray-700" data-id="element-9878">
                    Delegate inherits original approver's upper limit
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, a delegate will use the original approver's FDOA limit instead of their own" data-id="element-9879" />
                </div>
                <div className="flex items-center" data-id="element-9880">
                  <input id="original-chain" type="checkbox" checked={ruleData.delegation.useOriginalChain} onChange={e => handleDelegationChange('useOriginalChain', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9881" />
                  <label htmlFor="original-chain" className="ml-3 block text-sm text-gray-700" data-id="element-9882">
                    Escalate along original approver's manager chain
                  </label>
                  <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="If enabled, escalation will follow the original approver's management chain rather than the delegate's" data-id="element-9883" />
                </div>
                <div data-id="element-9884">
                  <label htmlFor="max-hops" className="block text-sm font-medium text-gray-700" data-id="element-9885">
                    Maximum Escalation Hops
                  </label>
                  <div className="mt-1 w-24" data-id="element-9886">
                    <input type="number" id="max-hops" min="1" max="10" value={ruleData.delegation.maxHops} onChange={e => handleDelegationChange('maxHops', parseInt(e.target.value))} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9887" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-9888">
                    Maximum number of manager levels to escalate through
                  </p>
                </div>
              </div>
            </div>}
        </div>
        {/* Category Approvers Section */}
        <div className="border-b border-gray-200" data-id="element-9889">
          <button type="button" onClick={() => toggleSection('categories')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9890">
            <div className="flex items-center" data-id="element-9891">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9892" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9893">
                Category Approvers
              </h3>
            </div>
            {expandedSections.categories ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9894" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9895" />}
          </button>
          {expandedSections.categories && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9896">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9897">
                Configure approvers for each GL category (cost centre)
              </p>
              <div className="space-y-4" data-id="element-9898">
                {categoryApprovers.map((approver, index) => <div key={approver.id} className="border border-gray-200 rounded-md p-4" data-id="element-9899">
                    <div className="flex justify-between items-start" data-id="element-9900">
                      <div className="flex items-center" data-id="element-9901">
                        <LayersIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-9902" />
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-9903">
                          Category Approver (Sequence {approver.sequence})
                        </h4>
                      </div>
                      <button type="button" onClick={() => removeCategoryApprover(index)} className="text-red-600 hover:text-red-900" data-id="element-9904">
                        <TrashIcon className="h-4 w-4" data-id="element-9905" />
                      </button>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9906">
                      <div className="sm:col-span-3" data-id="element-9907">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9908">
                          GL Category
                        </label>
                        <select value={approver.categoryId} onChange={e => updateCategoryApprover(index, 'categoryId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9909">
                          <option value="" data-id="element-9910">Select GL Category</option>
                          {glCategories.map(category => <option key={category.id} value={category.id} data-id="element-9911">
                              {category.code} - {category.description}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-1" data-id="element-9912">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9913">
                          Sequence
                        </label>
                        <input type="number" min="1" value={approver.sequence} onChange={e => updateCategoryApprover(index, 'sequence', parseInt(e.target.value))} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9914" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-9915">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9916">
                          User or Role
                        </label>
                        <select value={approver.userOrRoleId} onChange={e => updateCategoryApprover(index, 'userOrRoleId', e.target.value)} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-9917">
                          <option value="" data-id="element-9918">Select User or Role</option>
                          <optgroup label="Users" data-id="element-9919">
                            {users.map(user => <option key={user.id} value={user.id} data-id="element-9920">
                                {user.name}
                              </option>)}
                          </optgroup>
                          <optgroup label="Roles" data-id="element-9921">
                            {roles.map(role => <option key={role.id} value={role.id} data-id="element-9922">
                                {role.name}
                              </option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-9923">
                        <label className="block text-xs font-medium text-gray-700" data-id="element-9924">
                          Approval Limit
                        </label>
                        <div className="mt-1 flex rounded-md shadow-sm" data-id="element-9925">
                          <input type="number" value={approver.limit} onChange={e => updateCategoryApprover(index, 'limit', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-9926" />
                          <select value={approver.currency} onChange={e => updateCategoryApprover(index, 'currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-9927">
                            {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-9928">
                                {currency.code}
                              </option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}
                <button type="button" onClick={addCategoryApprover} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9929">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-9930" />
                  Add Category Approver
                </button>
              </div>
            </div>}
        </div>
        {/* Four-Eyes Section */}
        <div className="border-b border-gray-200" data-id="element-9931">
          <button type="button" onClick={() => toggleSection('fourEyes')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9932">
            <div className="flex items-center" data-id="element-9933">
              <EyeIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9934" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9935">
                Four-Eyes Principle
              </h3>
            </div>
            {expandedSections.fourEyes ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9936" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9937" />}
          </button>
          {expandedSections.fourEyes && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9938">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9939">
                Configure Four-Eyes principle requiring two distinct approvers
              </p>
              <div className="flex items-center mb-4" data-id="element-9940">
                <input id="enable-four-eyes" type="checkbox" checked={ruleData.fourEyes.enabled} onChange={e => handleFourEyesChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9941" />
                <label htmlFor="enable-four-eyes" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9942">
                  Enable Four-Eyes Principle
                </label>
              </div>
              {ruleData.fourEyes.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-9943">
                  <div data-id="element-9944">
                    <label htmlFor="four-eyes-threshold" className="block text-sm font-medium text-gray-700" data-id="element-9945">
                      Threshold
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm" data-id="element-9946">
                      <input type="number" id="four-eyes-threshold" value={ruleData.fourEyes.threshold} onChange={e => handleFourEyesChange('threshold', parseFloat(e.target.value))} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300" data-id="element-9947" />
                      <select value={ruleData.fourEyes.currency} onChange={e => handleFourEyesChange('currency', e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-9948">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-9949">
                            {currency.code}
                          </option>)}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9950">
                      Four-Eyes principle applies to transactions above this
                      amount
                    </p>
                  </div>
                  <div data-id="element-9951">
                    <label htmlFor="four-eyes-apply" className="block text-sm font-medium text-gray-700" data-id="element-9952">
                      Apply At
                    </label>
                    <select id="four-eyes-apply" value={ruleData.fourEyes.applyAt} onChange={e => handleFourEyesChange('applyAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9953">
                      <option value="Header" data-id="element-9954">Header</option>
                      <option value="Line" data-id="element-9955">Line</option>
                      <option value="Both" data-id="element-9956">Both</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9957">
                      Where to apply the Four-Eyes check
                    </p>
                  </div>
                </div>}
              <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-9958">
                <div className="flex" data-id="element-9959">
                  <div className="flex-shrink-0" data-id="element-9960">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-9961" />
                  </div>
                  <div className="ml-3" data-id="element-9962">
                    <h3 className="text-sm font-medium text-yellow-800" data-id="element-9963">
                      Four-Eyes Principle Note
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700" data-id="element-9964">
                      <p data-id="element-9965">When enabled, Four-Eyes requires that:</p>
                      <ul className="list-disc pl-5 mt-1 space-y-1" data-id="element-9966">
                        <li data-id="element-9967">
                          Two distinct approvers must approve transactions above
                          the threshold
                        </li>
                        <li data-id="element-9968">
                          The requester/coder of a document cannot be one of its
                          approvers
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* Emergency Approvals Section */}
        <div className="border-b border-gray-200" data-id="element-9969">
          <button type="button" onClick={() => toggleSection('emergency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-9970">
            <div className="flex items-center" data-id="element-9971">
              <AlertTriangleIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-9972" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-9973">
                Emergency Approvals
              </h3>
            </div>
            {expandedSections.emergency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-9974" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-9975" />}
          </button>
          {expandedSections.emergency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-9976">
              <p className="text-sm text-gray-500 mb-4" data-id="element-9977">
                Configure emergency approvers who can short-circuit normal
                approval flows
              </p>
              <div className="flex items-center mb-4" data-id="element-9978">
                <input id="enable-emergency" type="checkbox" checked={ruleData.emergency.enabled} onChange={e => handleEmergencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-9979" />
                <label htmlFor="enable-emergency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-9980">
                  Enable Emergency Approvals
                </label>
              </div>
              {ruleData.emergency.enabled && <div data-id="element-9981">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-9982">
                    Emergency Approvers
                  </label>
                  <div className="mb-4" data-id="element-9983">
                    <select id="emergency-approver-select" onChange={e => {
                if (e.target.value) {
                  addEmergencyApprover(e.target.value);
                  e.target.value = '';
                }
              }} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-9984">
                      <option value="" data-id="element-9985">Add Emergency Approver</option>
                      <optgroup label="Users" data-id="element-9986">
                        {users.filter(user => !ruleData.emergency.approvers.includes(user.id)).map(user => <option key={user.id} value={user.id} data-id="element-9987">
                              {user.name}
                            </option>)}
                      </optgroup>
                      <optgroup label="Roles" data-id="element-9988">
                        {roles.filter(role => !ruleData.emergency.approvers.includes(role.id)).map(role => <option key={role.id} value={role.id} data-id="element-9989">
                              {role.name}
                            </option>)}
                      </optgroup>
                    </select>
                  </div>
                  {ruleData.emergency.approvers.length > 0 ? <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-9990">
                      {ruleData.emergency.approvers.map(approverId => {
                const user = users.find(u => u.id === approverId);
                const role = roles.find(r => r.id === approverId);
                const name = user?.name || role?.name || 'Unknown';
                const isUser = !!user;
                return <li key={approverId} className="px-4 py-3 flex items-center justify-between" data-id="element-9991">
                            <div className="flex items-center" data-id="element-9992">
                              {isUser ? <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-9993" /> : <UsersIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-9994" />}
                              <span className="text-sm text-gray-900" data-id="element-9995">
                                {name}
                              </span>
                            </div>
                            <button type="button" onClick={() => removeEmergencyApprover(approverId)} className="text-red-600 hover:text-red-900" data-id="element-9996">
                              <TrashIcon className="h-4 w-4" data-id="element-9997" />
                            </button>
                          </li>;
              })}
                    </ul> : <div className="text-sm text-gray-500 italic" data-id="element-9998">
                      No emergency approvers selected
                    </div>}
                  <div className="mt-4 bg-yellow-50 p-4 rounded-md" data-id="element-9999">
                    <div className="flex" data-id="element-10000">
                      <div className="flex-shrink-0" data-id="element-10001">
                        <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-10002" />
                      </div>
                      <div className="ml-3" data-id="element-10003">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-10004">
                          Emergency Approval Note
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-10005">
                          <p data-id="element-10006">
                            Emergency approvers can short-circuit normal
                            approval flows in urgent situations. The Four-Eyes
                            principle will still be enforced if enabled.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
        {/* Line-Level Evaluation Section */}
        <div className="border-b border-gray-200" data-id="element-10007">
          <button type="button" onClick={() => toggleSection('lineLevel')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-10008">
            <div className="flex items-center" data-id="element-10009">
              <LayersIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-10010" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-10011">
                Line-Level Evaluation
              </h3>
            </div>
            {expandedSections.lineLevel ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-10012" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-10013" />}
          </button>
          {expandedSections.lineLevel && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-10014">
              <p className="text-sm text-gray-500 mb-4" data-id="element-10015">
                Configure line-level and multi-cost-centre evaluation
              </p>
              <div className="space-y-4" data-id="element-10016">
                <div className="flex items-center" data-id="element-10017">
                  <input id="enable-line-level" type="checkbox" checked={ruleData.lineLevel.enabled} onChange={e => handleLineLevelChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10018" />
                  <label htmlFor="enable-line-level" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10019">
                    Enable Line-Level Evaluation
                  </label>
                </div>
                {ruleData.lineLevel.enabled && <>
                    <div data-id="element-10020">
                      <label htmlFor="evaluation-mode" className="block text-sm font-medium text-gray-700" data-id="element-10021">
                        Evaluation Mode
                      </label>
                      <select id="evaluation-mode" value={ruleData.lineLevel.evaluationMode} onChange={e => handleLineLevelChange('evaluationMode', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10022">
                        <option value="Sequential" data-id="element-10023">Sequential</option>
                        <option value="Parallel" data-id="element-10024">Parallel</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500" data-id="element-10025">
                        Sequential: Lines are approved in sequence. Parallel:
                        All lines can be approved simultaneously.
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-10026">
                      <input id="categories-only" type="checkbox" checked={ruleData.lineLevel.categoriesOnly} onChange={e => handleLineLevelChange('categoriesOnly', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10027" />
                      <label htmlFor="categories-only" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10028">
                        Approvers only for categories they're authorised for
                      </label>
                      <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, approvers will only see and approve lines for GL categories they are authorized for" data-id="element-10029" />
                    </div>
                  </>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-10030">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-10031">
                    Line-Level Evaluation:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-10032">
                    <li data-id="element-10033">
                      Evaluates each line item separately based on its GL
                      code/cost centre
                    </li>
                    <li data-id="element-10034">
                      Allows for different approvers for different types of
                      expenses
                    </li>
                    <li data-id="element-10035">Can handle documents with multiple cost centres</li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Accounting Periods Section */}
        <div className="border-b border-gray-200" data-id="element-10036">
          <button type="button" onClick={() => toggleSection('periods')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-10037">
            <div className="flex items-center" data-id="element-10038">
              <CalendarIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-10039" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-10040">
                Accounting Periods
              </h3>
            </div>
            {expandedSections.periods ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-10041" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-10042" />}
          </button>
          {expandedSections.periods && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-10043">
              <p className="text-sm text-gray-500 mb-4" data-id="element-10044">
                Configure accounting periods and closed period checks
              </p>
              <div className="flex items-center mb-4" data-id="element-10045">
                <input id="check-closed-periods" type="checkbox" checked={ruleData.preApproval.checkClosedPeriods} onChange={e => setRuleData({
              ...ruleData,
              preApproval: {
                ...ruleData.preApproval,
                checkClosedPeriods: e.target.checked
              }
            })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10046" />
                <label htmlFor="check-closed-periods" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10047">
                  Check for closed accounting periods
                </label>
              </div>
              {ruleData.preApproval.checkClosedPeriods && <div className="space-y-4" data-id="element-10048">
                  <div className="overflow-x-auto" data-id="element-10049">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-10050">
                      <thead className="bg-gray-50" data-id="element-10051">
                        <tr data-id="element-10052">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10053">
                            Entity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10054">
                            Period Code
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10055">
                            Start Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10056">
                            End Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10057">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10058">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-10059">
                        {accountingPeriods.map((period, index) => {
                    const entity = entities.find(e => e.id === period.entityId);
                    return <tr key={period.id} data-id="element-10060">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10061">
                                {entity?.name || 'Unknown Entity'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10062">
                                {period.periodCode}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10063">
                                {new Date(period.startUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-10064">
                                {new Date(period.endUtc).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-10065">
                                {period.isClosed ? <StatusBadge status="error" text="Closed" data-id="element-10066" /> : <StatusBadge status="success" text="Open" data-id="element-10067" />}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10068">
                                <div className="flex justify-end space-x-2" data-id="element-10069">
                                  <button type="button" onClick={() => updateAccountingPeriod(index, 'isClosed', !period.isClosed)} className={period.isClosed ? 'text-green-600 hover:text-green-900' : 'text-amber-600 hover:text-amber-900'} title={period.isClosed ? 'Open Period' : 'Close Period'} data-id="element-10070">
                                    {period.isClosed ? <CheckIcon className="h-4 w-4" data-id="element-10071" /> : <LockIcon className="h-4 w-4" data-id="element-10072" />}
                                  </button>
                                  <button type="button" onClick={() => removeAccountingPeriod(index)} className="text-red-600 hover:text-red-900" title="Remove Period" data-id="element-10073">
                                    <TrashIcon className="h-4 w-4" data-id="element-10074" />
                                  </button>
                                </div>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={addAccountingPeriod} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10075">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-10076" />
                    Add Accounting Period
                  </button>
                </div>}
            </div>}
        </div>
        {/* Multi-Currency Section */}
        <div className="border-b border-gray-200" data-id="element-10077">
          <button type="button" onClick={() => toggleSection('multiCurrency')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-10078">
            <div className="flex items-center" data-id="element-10079">
              <DollarSignIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-10080" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-10081">
                Multi-Currency
              </h3>
            </div>
            {expandedSections.multiCurrency ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-10082" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-10083" />}
          </button>
          {expandedSections.multiCurrency && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-10084">
              <p className="text-sm text-gray-500 mb-4" data-id="element-10085">
                Configure multi-currency support
              </p>
              <div className="space-y-4" data-id="element-10086">
                <div className="flex items-center" data-id="element-10087">
                  <input id="enable-multi-currency" type="checkbox" checked={ruleData.multiCurrency.enabled} onChange={e => handleMultiCurrencyChange('enabled', e.target.checked)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10088" />
                  <label htmlFor="enable-multi-currency" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10089">
                    Enable Multi-Currency Support
                  </label>
                </div>
                {ruleData.multiCurrency.enabled && <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-10090">
                    <div data-id="element-10091">
                      <label htmlFor="base-currency" className="block text-sm font-medium text-gray-700" data-id="element-10092">
                        Base Currency
                      </label>
                      <select id="base-currency" value={ruleData.multiCurrency.baseCurrency} onChange={e => handleMultiCurrencyChange('baseCurrency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10093">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-10094">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-10095">
                      <label htmlFor="evaluate-at" className="block text-sm font-medium text-gray-700" data-id="element-10096">
                        Evaluate Thresholds At
                      </label>
                      <select id="evaluate-at" value={ruleData.multiCurrency.evaluateAt} onChange={e => handleMultiCurrencyChange('evaluateAt', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-10097">
                        <option value="Header" data-id="element-10098">Header (Document Total)</option>
                        <option value="Line" data-id="element-10099">Line (Line Subtotals)</option>
                      </select>
                    </div>
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-10100">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-10101">
                    Multi-Currency Support:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-10102">
                    <li data-id="element-10103">
                      Converts transaction amounts to the base currency for
                      threshold evaluation
                    </li>
                    <li data-id="element-10104">Uses current exchange rates from the system</li>
                    <li data-id="element-10105">
                      Allows approvers to have limits in different currencies
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        {/* Audit Section */}
        <div className="border-b border-gray-200" data-id="element-10106">
          <button type="button" onClick={() => toggleSection('audit')} className="px-4 py-5 sm:px-6 w-full flex justify-between items-center text-left" data-id="element-10107">
            <div className="flex items-center" data-id="element-10108">
              <ClockIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-10109" />
              <h3 className="text-base font-medium text-gray-900" data-id="element-10110">Audit</h3>
            </div>
            {expandedSections.audit ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-10111" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-10112" />}
          </button>
          {expandedSections.audit && <div className="px-4 py-5 sm:p-6 border-t border-gray-200" data-id="element-10113">
              <p className="text-sm text-gray-500 mb-4" data-id="element-10114">
                Configure audit settings
              </p>
              <div className="space-y-4" data-id="element-10115">
                <div className="flex items-center" data-id="element-10116">
                  <input id="enable-audit" type="checkbox" checked={ruleData.audit.enabled} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  enabled: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10117" />
                  <label htmlFor="enable-audit" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10118">
                    Enable Audit Logging
                  </label>
                </div>
                {ruleData.audit.enabled && <div className="flex items-center" data-id="element-10119">
                    <input id="store-decisions" type="checkbox" checked={ruleData.audit.storeDecisions} onChange={e => setRuleData({
                ...ruleData,
                audit: {
                  ...ruleData.audit,
                  storeDecisions: e.target.checked
                }
              })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-10120" />
                    <label htmlFor="store-decisions" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-10121">
                      Store Rule Decisions
                    </label>
                    <HelpCircleIcon className="h-4 w-4 text-gray-400 ml-2" title="When enabled, all rule decisions (Approved, Escalated, Failed, etc.) will be stored in the audit log" data-id="element-10122" />
                  </div>}
                <div className="mt-4 bg-gray-50 p-4 rounded-md" data-id="element-10123">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-10124">
                    Audit Features:
                  </h5>
                  <ul className="text-xs text-gray-600 space-y-2" data-id="element-10125">
                    <li data-id="element-10126">
                      Append-only audit log ensures all rule evaluations are
                      recorded
                    </li>
                    <li data-id="element-10127">
                      Tamper-evident hashing ensures audit trail integrity
                    </li>
                    <li data-id="element-10128">
                      Compliant with ISO27001 and other regulatory requirements
                    </li>
                  </ul>
                </div>
              </div>
            </div>}
        </div>
      </div>
      <div className="flex justify-end space-x-3" data-id="element-10129">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10130">
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10131">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-10132" />
          Save Rule
        </button>
      </div>
    </form>;
}